import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { GitBranch, PlayCircle, CheckCircle2, Circle, ChevronRight, Plus, X, AlertTriangle, XCircle, Clock, Filter } from "lucide-react";

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-800",
  in_progress: "bg-blue-100 text-blue-800",
  complete: "bg-emerald-100 text-emerald-800",
  completed: "bg-blue-100 text-blue-800",
  paused: "bg-amber-100 text-amber-800",
  draft: "bg-zinc-100 text-zinc-700",
};

export default function WorkflowEngine() {
  const [templates, setTemplates] = useState([]);
  const [instances, setInstances] = useState([]);
  const [overdueInstances, setOverdueInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("instances");
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", module_type: "", steps: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [allInstances, setAllInstances] = useState([]);
  const [stepAssignees, setStepAssignees] = useState({});

  const load = () => {
    Promise.all([
      api.get("/workflow-templates").then((r) => setTemplates(r.data)).catch(() => {}),
      api.get("/workflow-instances").then((r) => setInstances(r.data)).catch(() => {}),
      api.get("/workflow-instances/overdue").then((r) => setOverdueInstances(r.data)).catch(() => {}),
      api.get("/workflow-instances?include_completed=true").then((r) => setAllInstances(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const createTemplate = async () => {
    setSaving(true);
    try {
      const steps = form.steps.split(",").map((s) => s.trim()).filter(Boolean).map((name) => ({ step_name: name, required: true }));
      await api.post("/workflow-templates", { name: form.name, module_type: form.module_type, description: form.description, steps, active: true });
      toast.success("Template created");
      setShowCreate(false);
      setForm({ name: "", module_type: "", steps: "", description: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const advanceStep = async (instanceId) => {
    try {
      await api.post(`/workflow-instances/${instanceId}/advance`, {});
      toast.success("Step advanced");
      load();
      if (selectedInstance) {
        api.get(`/workflow-instances/${instanceId}`).then((r) => setSelectedInstance(r.data)).catch(() => {});
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const rejectStep = async (instanceId) => {
    try {
      await api.post(`/workflow-instances/${instanceId}/reject`, { reason: "Rejected by user" });
      toast.success("Step rejected — reverted to previous step");
      load();
      if (selectedInstance) {
        api.get(`/workflow-instances/${instanceId}`).then((r) => setSelectedInstance(r.data)).catch(() => {});
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const isOverdue = (instId) => overdueInstances.some((o) => o.id === instId);

  const launchWorkflow = async (templateId) => {
    try {
      const crypto = window.crypto || {};
      const rid = crypto.randomUUID ? crypto.randomUUID() : "00000000-0000-0000-0000-000000000000";
      await api.post("/workflow-instances", { template_id: templateId, record_type: "manual", record_id: rid });
      toast.success("Workflow launched");
      setTab("instances");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const loadInstance = (inst) => {
    api.get(`/workflow-instances/${inst.id}`).then((r) => setSelectedInstance(r.data)).catch(() => setSelectedInstance(inst));
  };

  const assignStep = async (instanceId, assignee) => {
    try {
      await api.patch(`/workflow-instances/${instanceId}`, { assignee });
      toast.success("Assignee updated");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const filteredInstances = statusFilter === "all"
    ? instances
    : instances.filter((inst) => inst.status === statusFilter);

  const groupedHistory = {
    active: allInstances.filter((i) => i.status === "active" || i.status === "in_progress"),
    paused: allInstances.filter((i) => i.status === "paused"),
    completed: allInstances.filter((i) => i.status === "completed" || i.status === "complete"),
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Workflow Engine</h1>
          <p className="text-zinc-500 mt-2 text-sm">Manage workflow templates and track active instances.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> New template
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-zinc-200">
        <button onClick={() => setTab("instances")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === "instances" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>
          Active Workflows ({instances.length})
        </button>
        <button onClick={() => setTab("templates")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === "templates" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>
          Templates ({templates.length})
        </button>
        <button onClick={() => setTab("history")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === "history" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>
          History ({allInstances.length})
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : tab === "templates" ? (
        templates.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <GitBranch size={26} className="mx-auto mb-3" />
            <p className="text-sm">No workflow templates yet.</p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map((t) => (
              <div key={t.id} className="border border-zinc-200 bg-white rounded-lg p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium">{t.name}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{t.description}</p>
                  </div>
                  <button onClick={() => launchWorkflow(t.id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-zinc-950 text-white rounded-md text-xs font-medium hover:bg-zinc-800">
                    <PlayCircle size={12} /> Launch
                  </button>
                </div>
                {t.steps && (
                  <div className="mt-3 flex items-center gap-1 flex-wrap">
                    {t.steps.map((s, i) => (
                      <span key={i} className="inline-flex items-center gap-1">
                        <span className="text-[10px] bg-zinc-100 px-2 py-0.5 rounded">{s.name}</span>
                        {i < t.steps.length - 1 && <ChevronRight size={10} className="text-zinc-300" />}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : tab === "history" ? (
        <div className="mt-6">
          {allInstances.length === 0 ? (
            <div className="border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
              <Clock size={26} className="mx-auto mb-3" />
              <p className="text-sm">No workflow history yet.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {[
                { key: "active", label: "Active" },
                { key: "paused", label: "Paused" },
                { key: "completed", label: "Completed" },
              ].map(({ key, label }) => groupedHistory[key].length > 0 && (
                <div key={key}>
                  <h3 className="font-heading font-semibold text-sm mb-3">{label} ({groupedHistory[key].length})</h3>
                  <div className="border border-zinc-200 bg-white rounded-lg overflow-hidden">
                    {groupedHistory[key].map((inst) => (
                      <div key={inst.id} className="px-6 py-3 border-b border-zinc-50 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">{inst.name}</p>
                          <p className="text-xs text-zinc-400 mt-0.5">{inst.current_step_name || "N/A"}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {(key === "completed") && (
                            <span className="text-xs text-zinc-400">
                              {inst.completed_at ? new Date(inst.completed_at).toLocaleDateString() : ""}
                              {inst.total_steps ? ` · ${inst.total_steps} steps` : ""}
                            </span>
                          )}
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[inst.status] || STATUS_STYLE.draft}`}>{inst.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-6">
          {/* Status Filter */}
          <div className="mb-4 flex items-center gap-2">
            <Filter size={14} className="text-zinc-400" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-zinc-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div className="flex gap-6">
          {filteredInstances.length === 0 ? (
            <div className="flex-1 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
              <GitBranch size={26} className="mx-auto mb-3" />
              <p className="text-sm">{statusFilter === "all" ? "No active workflows. Launch one from a template." : `No ${statusFilter} workflows.`}</p>
            </div>
          ) : (
            <>
              <div className="flex-1 border border-zinc-200 bg-white rounded-lg overflow-hidden">
                <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                  <span className="col-span-4">Workflow</span>
                  <span className="col-span-3">Current Step</span>
                  <span className="col-span-2">Progress</span>
                  <span className="col-span-2">Status</span>
                  <span className="col-span-1 text-right"></span>
                </div>
                {filteredInstances.map((inst) => {
                  const progress = inst.total_steps ? Math.round((inst.completed_steps / inst.total_steps) * 100) : 0;
                  return (
                    <div key={inst.id} className={`grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50 cursor-pointer hover:bg-zinc-50 ${selectedInstance?.id === inst.id ? "bg-zinc-50" : ""}`}
                      onClick={() => loadInstance(inst)}>
                      <span className="col-span-4 text-sm font-medium">{inst.name}</span>
                      <span className="col-span-3 text-sm text-zinc-600">{inst.current_step_name || "N/A"}</span>
                      <span className="col-span-2">
                        <div className="w-full bg-zinc-100 rounded-full h-1.5">
                          <div className="bg-zinc-800 h-1.5 rounded-full" style={{ width: `${progress}%` }} />
                        </div>
                        <span className="text-[10px] text-zinc-400">{progress}%</span>
                      </span>
                      <span className="col-span-2 flex items-center gap-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[inst.status] || STATUS_STYLE.draft}`}>{inst.status}</span>
                        {isOverdue(inst.id) && <AlertTriangle size={12} className="text-red-500" title="Overdue" />}
                      </span>
                      <span className="col-span-1 text-right"><ChevronRight size={14} className="inline text-zinc-400" /></span>
                    </div>
                  );
                })}
              </div>

              {/* Step Progress Panel */}
              {selectedInstance && (
                <div className="w-80 shrink-0 border border-zinc-200 bg-white rounded-lg p-6">
                  <h3 className="font-heading font-semibold text-sm">{selectedInstance.name}</h3>

                  {/* SLA Deadline */}
                  {selectedInstance.sla_deadline && (
                    <div className="mt-3">
                      {(() => {
                        const deadline = new Date(selectedInstance.sla_deadline);
                        const isPast = deadline < new Date();
                        return (
                          <div className={`flex items-center gap-1.5 text-xs ${isPast ? "text-red-600" : "text-zinc-500"}`}>
                            <Clock size={12} />
                            <span>SLA: {deadline.toLocaleDateString()}</span>
                            {isPast && <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-[10px] font-medium">Overdue</span>}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <div className="mt-4 space-y-3">
                    {(selectedInstance.steps || []).map((step, i) => (
                      <div key={i} className="flex items-start gap-2">
                        {step.status === "completed" ? (
                          <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                        ) : step.status === "active" ? (
                          <PlayCircle size={16} className="text-blue-500 mt-0.5 shrink-0" />
                        ) : (
                          <Circle size={16} className="text-zinc-300 mt-0.5 shrink-0" />
                        )}
                        <div className="flex-1">
                          <p className={`text-sm ${step.status === "active" ? "font-medium" : step.status === "completed" ? "text-zinc-500" : "text-zinc-400"}`}>{step.name}</p>
                          {step.status === "active" && (
                            <>
                              <div className="flex gap-2 mt-1">
                                <button onClick={() => advanceStep(selectedInstance.id)}
                                  className="text-xs text-blue-700 hover:text-blue-900 font-medium">Advance</button>
                                {(selectedInstance.current_step_index || 0) > 0 && (
                                  <button onClick={() => rejectStep(selectedInstance.id)}
                                    className="text-xs text-red-600 hover:text-red-800 font-medium inline-flex items-center gap-0.5"><XCircle size={10} /> Reject</button>
                                )}
                              </div>
                              <input
                                type="text"
                                placeholder="Assign to (name/email)"
                                value={stepAssignees[selectedInstance.id] ?? (selectedInstance.assignee || "")}
                                onChange={(e) => setStepAssignees({ ...stepAssignees, [selectedInstance.id]: e.target.value })}
                                onBlur={(e) => { if (e.target.value !== (selectedInstance.assignee || "")) assignStep(selectedInstance.id, e.target.value); }}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
                                className="mt-1.5 w-full border border-zinc-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-zinc-950"
                              />
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          </div>
        </div>
      )}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New workflow template</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Template name (e.g. Sample Approval)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.module_type} onChange={(e) => setForm({ ...form, module_type: e.target.value })} placeholder="Module type (e.g. sample, design, costing)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)"
                rows={3}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
              <input value={form.steps} onChange={(e) => setForm({ ...form, steps: e.target.value })} placeholder="Steps (comma-separated, e.g. Request, Review, Approve)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <button onClick={createTemplate} disabled={!form.name || !form.module_type || !form.steps || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create template"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
